import { useOutletContext, useNavigate } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, LineChart, Line } from "recharts";
import { UserPlus, TrendingUp, Zap, Moon } from "lucide-react";
import { KpiCard, PageHeader, Loading } from "@/components/portal/widgets";
import { ChartPanel, tooltipStyle, CompanyLink, ErrorNote, ExportButton } from "@/components/founder/ui";
import { useFounder, fmtMonth, daysAgo } from "@/lib/founderApi";

export default function FounderGrowth() {
  const { demo } = useOutletContext();
  const nav = useNavigate();
  const { data: o, error } = useFounder(`/platform/overview?include_demo=${demo}`);
  const { data: companies } = useFounder(`/platform/companies?include_demo=${demo}`);
  if (error) return <ErrorNote error={error} />;
  if (!o) return <Loading label="Loading acquisition…" />;
  const s = o.subscriptions || {};
  const list = companies || [];
  const activated = list.filter((c) => (c.usage?.trips || 0) > 0);
  const quiet = list.filter((c) => { const d = daysAgo(c.last_active); return d == null || d > 7; });
  const top = [...list].sort((a, b) => (b.usage?.trips_30d || 0) - (a.usage?.trips_30d || 0)).slice(0, 5);
  const growth = o.new_last_month ? Math.round(((o.new_this_month - o.new_last_month) / o.new_last_month) * 100) : null;
  const funnel = [
    { label: "Signed up", value: o.companies, to: "/founder/companies" },
    { label: "Imported trips", value: activated.length, to: "/founder/companies?sort=trips_30d" },
    { label: "On trial", value: s.trial || 0, to: "/founder/companies?status=trial" },
    { label: "Paying", value: s.paying || 0, to: "/founder/companies?status=active" },
  ];
  const max = Math.max(1, ...funnel.map((f) => f.value));

  return (
    <div className="space-y-8" data-testid="founder-growth">
      <PageHeader title="Acquisition" subtitle="Who is signing up, who gets value, and who is drifting away."><ExportButton kind="companies" demo={demo} /></PageHeader>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiCard icon={UserPlus} label="New this month" value={o.new_this_month} sub={growth == null ? `${o.new_last_month} last month` : `${growth >= 0 ? "+" : ""}${growth}% vs last month`} to={`/founder/companies?month=${new Date().toISOString().slice(0, 7)}`} testid="growth-kpi-new" />
        <KpiCard icon={Zap} label="Activated" accent="emerald" value={`${list.length ? Math.round((activated.length / list.length) * 100) : 0}%`} sub={`${activated.length} of ${list.length} have imported trips`} to="/founder/companies?sort=trips_30d" testid="growth-kpi-activated" />
        <KpiCard icon={TrendingUp} label="Trial → paid" accent="amber" value={o.conversion_rate == null ? "—" : `${Math.round(o.conversion_rate * (o.conversion_rate <= 1 ? 100 : 1))}%`} sub={(s.paying || 0) + (s.trial_ended || 0) + (s.canceled || 0) ? `${s.paying || 0} paying · ${s.trial_ended || 0} trials ended · ${s.canceled || 0} canceled` : "No trials have finished yet"} testid="growth-kpi-conversion" to="/founder/companies?status=trial" />
        <KpiCard icon={Moon} label="Quiet 7+ days" accent="red" value={quiet.length} sub="Haven't opened RideStamp in a week" to="/founder/companies?sort=last_active" testid="growth-kpi-quiet" />
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <ChartPanel title="Sign-ups per week" hint="Last 8 weeks" testid="growth-chart-weekly">
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={o.signups_weekly}>
              <CartesianGrid stroke="#1e293b" vertical={false} />
              <XAxis dataKey="label" stroke="#64748b" fontSize={11} />
              <YAxis stroke="#64748b" fontSize={11} allowDecimals={false} width={30} />
              <Tooltip {...tooltipStyle} formatter={(v) => [v, "Companies"]} labelFormatter={(l) => `Week of ${l}`} />
              <Line dataKey="count" stroke="#60a5fa" strokeWidth={2.5} dot={{ r: 3, fill: "#60a5fa" }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartPanel>
        <ChartPanel title="Sign-ups per month" hint="Tap a month to see who joined" testid="growth-chart-monthly">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={o.signups_monthly}>
              <CartesianGrid stroke="#1e293b" vertical={false} />
              <XAxis dataKey="month" tickFormatter={fmtMonth} stroke="#64748b" fontSize={11} />
              <YAxis stroke="#64748b" fontSize={11} allowDecimals={false} width={30} />
              <Tooltip {...tooltipStyle} labelFormatter={fmtMonth} formatter={(v) => [v, "Companies"]} />
              <Bar dataKey="count" fill="#3b82f6" radius={[4, 4, 0, 0]} cursor="pointer" onClick={(d) => nav(`/founder/companies?month=${d.month || d.payload?.month}`)} />
            </BarChart>
          </ResponsiveContainer>
        </ChartPanel>
      </div>
      <div className="grid gap-4 lg:grid-cols-3">
        <ChartPanel title="Funnel" hint="Tap a step to see those companies" testid="growth-funnel">
          <div className="space-y-3">
            {funnel.map((f) => (
              <button key={f.label} onClick={() => nav(f.to)} className="block w-full text-left" data-testid={`funnel-${f.label.toLowerCase().replace(/ /g, "-")}`}>
                <div className="mb-1 flex justify-between text-xs"><span className="text-slate-300">{f.label}</span><span className="font-mono-data text-white">{f.value}</span></div>
                <div className="h-2.5 rounded-full bg-slate-800"><div className="h-2.5 rounded-full bg-gradient-to-r from-blue-600 to-blue-400" style={{ width: `${(f.value / max) * 100}%` }} /></div>
              </button>
            ))}
          </div>
        </ChartPanel>
        <ListPanel title="Most active (30 days)" testid="growth-top" items={top} right={(c) => `${c.usage?.trips_30d || 0} trips`} />
        <ListPanel title="Going quiet" testid="growth-quiet" items={quiet} empty="Everyone has been active this week." right={(c) => { const d = daysAgo(c.last_active); return d == null ? "Never active" : `${d}d ago`; }} />
      </div>
    </div>
  );
}

const ListPanel = ({ title, items, right, testid, empty = "No companies yet." }) => (
  <ChartPanel title={title} testid={testid}>
    <ul className="divide-y divide-slate-800">
      {items.map((c) => (
        <li key={c.id} className="flex items-center justify-between gap-3 py-2 text-sm">
          <CompanyLink id={c.id} name={c.name} testid={`${testid}-${c.id}`} />
          <span className="text-xs text-slate-400">{right(c)}</span>
        </li>
      ))}
      {!items.length && <li className="py-2 text-sm text-slate-500">{empty}</li>}
    </ul>
  </ChartPanel>
);
